import { Button, Card, Form, Modal, Select, Skeleton, Space, Switch } from "antd";
import { useEffect, useMemo, useState } from "react";
import DescriptionEditor from "../components/DescriptionEditor";
import apiPath from "../constants/apiPath";
import useRequest from "../hooks/useRequest";
import { Severty, ShowToast } from "../helper/toast";

const PAGE_OPTIONS = [
  { slug: "about-us", title: "About Us" },
  { slug: "terms-of-use", title: "Terms of Use" },
  { slug: "privacy-policy", title: "Privacy Policy" },
  { slug: "contact-us", title: "Contact Us" },
];

const hasMeaningfulHtmlContent = (html) => {
  const text = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 0;
};

const ContentManagerForm = () => {
  const { request } = useRequest();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [pagesMap, setPagesMap] = useState({});
  const [selectedSlug, setSelectedSlug] = useState("about-us");
  const [editorHtml, setEditorHtml] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [contentError, setContentError] = useState("");

  const selectedPageTitle = useMemo(() => {
    return PAGE_OPTIONS.find((p) => p.slug === selectedSlug)?.title || "Content Page";
  }, [selectedSlug]);

  const getPages = async () => {
    setLoading(true);
    try {
      await request({
        url: apiPath.authAdminContentPages,
        method: "GET",
        onSuccess: (data) => {
          const pages = data?.pages || [];
          const nextMap = {};
          pages.forEach((p) => {
            nextMap[p.slug] = p;
          });
          setPagesMap(nextMap);
        },
        onError: (err) => {
          const msg = err?.response?.data?.message || err?.message || "Failed to load content pages";
          ShowToast(msg, Severty.ERROR);
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;
    const page = pagesMap[selectedSlug];
    setEditorHtml(page?.contentHtml || "");
    setIsPublished(typeof page?.isPublished === "boolean" ? page.isPublished : true);
    setContentError("");
    form.setFieldsValue({ contentHtml: page?.contentHtml || "", isPublished: page?.isPublished ?? true });
  }, [selectedSlug, pagesMap, form, loading]);

  const saveCurrentPage = async () => {
    if (!hasMeaningfulHtmlContent(editorHtml)) {
      setContentError("Content is required");
      return;
    }
    setContentError("");
    setSaving(true);
    try {
      await request({
        url: `${apiPath.authAdminContentPages}/${selectedSlug}`,
        method: "PUT",
        data: {
          title: selectedPageTitle,
          contentHtml: editorHtml || "",
          isPublished,
        },
        onSuccess: (data) => {
          ShowToast(data?.message || "Content updated successfully", Severty.SUCCESS);
          setPagesMap((prev) => ({
            ...prev,
            [selectedSlug]: data?.page || {
              slug: selectedSlug,
              title: selectedPageTitle,
              contentHtml: editorHtml,
              isPublished,
            },
          }));
        },
        onError: (err) => {
          const msg = err?.response?.data?.message || err?.message || "Failed to save content";
          ShowToast(msg, Severty.ERROR);
        },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading ? (
        [1, 2, 3].map((item) => <Skeleton active key={item} />)
      ) : (
        <Card bordered={false} className="criclebox" title="Content Manager">
          <Form form={form} layout="vertical">
            <Space style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", width: "100%" }} wrap>
              <Space wrap>
                <Select
                  style={{ width: 220 }}
                  value={selectedSlug}
                  onChange={(v) => {
                    setSelectedSlug(v);
                    const nextPage = pagesMap[v];
                    setEditorHtml(nextPage?.contentHtml || "");
                    setIsPublished(
                      typeof nextPage?.isPublished === "boolean" ? nextPage.isPublished : true
                    );
                    setContentError("");
                    form.setFieldsValue({
                      contentHtml: nextPage?.contentHtml || "",
                      isPublished: nextPage?.isPublished ?? true,
                    });
                  }}
                  options={PAGE_OPTIONS.map((p) => ({ label: p.title, value: p.slug }))}
                />
                <div style={{  display: "flex", alignItems: "center", gap: 8 }}>
                  Published
                  <Switch checked={isPublished} onChange={setIsPublished} />
                </div>
              </Space>
              <Space>
                <Button onClick={() => setPreviewOpen(true)}>Preview</Button>
                <Button type="primary" loading={saving} onClick={saveCurrentPage}>
                  Save Changes
                </Button>
              </Space>
            </Space>

            <Form.Item
              label={`${selectedPageTitle} content`}
              required
              validateStatus={contentError ? "error" : undefined}
              help={contentError || undefined}
              style={{ marginBottom: 0 }}
            >
              <div className="content-manager-editor">
                <DescriptionEditor
                  key={selectedSlug}
                  value={editorHtml}
                  placeholder={`Edit ${selectedPageTitle} content`}
                  onChange={(html) => {
                    setEditorHtml(html);
                    if (contentError && hasMeaningfulHtmlContent(html)) {
                      setContentError("");
                    }
                  }}
                />
              </div>
            </Form.Item>
          </Form>
        </Card>
      )}

      <Modal
        open={previewOpen}
        title={`Preview - ${selectedPageTitle}`}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        width={900}
      >
        <div
          className="content-manager-preview"
          style={{
            minHeight: 240,
            maxHeight: "65vh",
            overflow: "auto",
            padding: 12,
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 8,
          }}
          dangerouslySetInnerHTML={{ __html: editorHtml || "<p>No content</p>" }}
        />
      </Modal>
    </>
  );
};

export default ContentManagerForm;
